import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Autocomplete,
  Box,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

const AssignSubsDialog = ({ open, onClose, groupId, groupName, fetchSubs }) => {
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allSubs, setAllSubs] = useState([]);
  const [error, setError] = useState(null);

  const fetchAllSubs = async () => {
    if (!groupId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/users/unlinked`,
        {
          params: {
            companyId: process.env.REACT_APP_COMPANY_ID,
            type: "subscribers",
            groupId,
          },
        }
      );
      setAllSubs(response.data || []);
    } catch (err) {
      console.error("Error fetching subscribers:", err);
      setError("Failed to load subscribers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAllSubs();
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!groupId || selectedSubs.length === 0) {
      toast.warning("Please select at least one subscriber");
      return;
    }
    try {
      const payload = {
        userIds: selectedSubs,
        groupId,
        UI_Description: `${getUIDescription()}\n Assign Subscribers to Report Group `,
      };
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/subscribers/`,
        payload
      );
      toast.success("Successfully Assigned Subscribers to Group Report!");
      setSelectedSubs([]);
      onClose();
      fetchSubs();
    } catch (err) {
      console.error("❌ Error assigning subscribers to report group:", err);
      toast.error("Failed to assign subscribers to report group");
    }
    onClose();
  };

  const handleCancel = () => {
    setSelectedSubs([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" component="span">
          {`Assign Subscriber(s) To ${groupName}`}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={{ xs: 2, md: 4 }} my={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Group Name"
              value={groupName}
              disabled
              fullWidth
              sx={{ mb: 2 }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              options={error ? [] : allSubs}
              getOptionLabel={(option) => option.name}
              // isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionKey={(option) => option.id}
              value={allSubs.filter((sub) => selectedSubs.includes(sub.id))}
              onChange={(event, newValue) => {
                setSelectedSubs(newValue.map((sub) => sub.id));
              }}
              multiple
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Subscriber"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              loading={loading}
              disableClearable
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button
            sx={{
              borderRadius: 0, // ✅ remove rounding
            }}
            onClick={handleSubmit}
            variant="contained"
            color="primary"
          >
            Assign
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AssignSubsDialog;
